import { http, toQuery } from '@/lib/apiClient';

export type WorkflowDocumentType = 'VENDOR_ONBOARDING' | 'PURCHASE_ORDER' | 'GOODS_RECEIPT' | 'VENDOR_INVOICE' | 'PAYMENT';
export type WorkflowStepType = 'CHECKER' | 'APPROVER';
export type WorkflowTaskAction = 'PENDING' | 'CHECKED' | 'APPROVED' | 'REJECTED' | 'RETURNED';
export type WorkflowInstanceStatus = 'IN_PROGRESS' | 'APPROVED' | 'REJECTED' | 'CANCELLED';

export interface PagedResult<T> { page: number; pageSize: number; totalCount: number; items: T[] }

export interface WorkflowStep {
  id?: string;
  stepNo: number;
  stepType: WorkflowStepType;
  name: string;
  approverRoleId: string;
  approverRoleCode?: string;
  minAmount: number | null;
  maxAmount: number | null;
  isMandatory: boolean;
  slaHours: number;
}

export interface WorkflowDefinition {
  id: string;
  code: string;
  name: string;
  documentType: WorkflowDocumentType;
  companyId: string | null;
  version: number;
  isActive: boolean;
  effectiveFrom: string;
  steps: WorkflowStep[];
}

export interface WorkflowTask {
  id: string;
  instanceId: string;
  stepNo: number;
  stepType: WorkflowStepType;
  assignedRoleId: string;
  assignedRoleCode: string;
  assignedRoleName?: string | null;
  assignedUserId: string | null;
  action: WorkflowTaskAction;
  actedBy: string | null;
  actedByName: string | null;
  actedAt: string | null;
  comments: string | null;
  documentType: WorkflowDocumentType;
  documentId: string;
  documentNo: string;
}

export interface WorkflowInstance {
  id: string;
  workflowDefinitionId: string;
  documentType: WorkflowDocumentType;
  documentId: string;
  documentNo: string;
  amount: number | null;
  currentStepNo: number;
  status: WorkflowInstanceStatus;
  initiatedBy: string;
  initiatedByName: string;
  initiatedAt: string;
  completedAt: string | null;
  tasks: WorkflowTask[];
}

type WorkflowTaskWire = Omit<Partial<WorkflowTask>, 'action'> & Pick<WorkflowTask, 'id'> & { action?: WorkflowTaskAction; status?: WorkflowTaskAction };
type WorkflowInstanceWire = Partial<WorkflowInstance> & Pick<WorkflowInstance, 'id' | 'documentType' | 'documentId' | 'status' | 'currentStepNo'> & { definitionId?: string; startedAt?: string; tasks?: WorkflowTaskWire[] };
const normalizeTask = (task: WorkflowTaskWire): WorkflowTask => ({
  id: task.id, instanceId: task.instanceId ?? '', stepNo: task.stepNo ?? 0, stepType: task.stepType ?? 'CHECKER',
  assignedRoleId: task.assignedRoleId ?? '', assignedRoleCode: task.assignedRoleCode ?? '', assignedRoleName: task.assignedRoleName ?? null, assignedUserId: task.assignedUserId ?? null,
  action: task.action ?? task.status ?? 'PENDING', actedBy: task.actedBy ?? null, actedByName: task.actedByName ?? null,
  actedAt: task.actedAt ?? null, comments: task.comments ?? null, documentType: task.documentType ?? 'VENDOR_ONBOARDING',
  documentId: task.documentId ?? '', documentNo: task.documentNo ?? '',
});
const normalizeInstance = (instance: WorkflowInstanceWire): WorkflowInstance => ({
  id: instance.id, workflowDefinitionId: instance.workflowDefinitionId ?? instance.definitionId ?? '', documentType: instance.documentType,
  documentId: instance.documentId, documentNo: instance.documentNo ?? '', amount: instance.amount ?? null, currentStepNo: instance.currentStepNo,
  status: instance.status, initiatedBy: instance.initiatedBy ?? '', initiatedByName: instance.initiatedByName ?? '',
  initiatedAt: instance.initiatedAt ?? instance.startedAt ?? '', completedAt: instance.completedAt ?? null,
  tasks: (instance.tasks ?? []).map(normalizeTask),
});

export interface WorkflowDefinitionQuery {
  page?: number; pageSize?: number; search?: string; documentType?: WorkflowDocumentType; companyId?: string; isActive?: boolean;
}
export interface WorkflowTaskQuery { page?: number; pageSize?: number; documentType?: WorkflowDocumentType; action?: WorkflowTaskAction }

export type WorkflowStepInput = Pick<WorkflowStep, 'stepNo' | 'stepType' | 'name' | 'approverRoleId' | 'isMandatory'>;
export interface CreateWorkflowDefinitionRequest {
  code: string; name: string; documentType: WorkflowDocumentType; companyId: string | null; steps: WorkflowStepInput[];
}

export const workflowDefinitionsApi = {
  list: (query: WorkflowDefinitionQuery = {}) => http.get<PagedResult<WorkflowDefinition>>(`/api/v1/workflow-definitions${toQuery({ ...query })}`),
  getById: (id: string) => http.get<WorkflowDefinition>(`/api/v1/workflow-definitions/${id}`),
  create: (body: CreateWorkflowDefinitionRequest) => http.post<WorkflowDefinition>('/api/v1/workflow-definitions', body),
  update: (id: string, body: { name: string; companyId: string | null }) => http.put<WorkflowDefinition>(`/api/v1/workflow-definitions/${id}`, body),
  replaceSteps: (id: string, steps: WorkflowStepInput[]) => http.put<WorkflowDefinition>(`/api/v1/workflow-definitions/${id}/steps`, { steps }),
  activate: (id: string) => http.post<void>(`/api/v1/workflow-definitions/${id}/activate`),
  deactivate: (id: string) => http.post<void>(`/api/v1/workflow-definitions/${id}/deactivate`),
};

export const workflowInstancesApi = {
  getById: (id: string) => http.get<WorkflowInstanceWire>(`/api/v1/workflow-instances/${id}`).then(normalizeInstance),
  getByDocument: (documentType: WorkflowDocumentType, documentId: string) => http.get<WorkflowInstanceWire>(`/api/v1/workflow-instances/by-document${toQuery({ documentType, documentId })}`).then(normalizeInstance),
};

export const workflowTasksApi = {
  my: (query: WorkflowTaskQuery = {}) => http.get<PagedResult<WorkflowTaskWire>>(`/api/v1/workflow-tasks/my${toQuery({ ...query })}`).then((page) => ({ ...page, items: page.items.map(normalizeTask) })),
  getById: (id: string) => http.get<WorkflowTaskWire>(`/api/v1/workflow-tasks/${id}`).then(normalizeTask),
  check: (id: string, comments: string) => http.post<WorkflowTaskWire>(`/api/v1/workflow-tasks/${id}/check`, { comments }).then(normalizeTask),
  approve: (id: string, comments: string) => http.post<WorkflowTaskWire>(`/api/v1/workflow-tasks/${id}/approve`, { comments }).then(normalizeTask),
  reject: (id: string, comments: string) => http.post<WorkflowTaskWire>(`/api/v1/workflow-tasks/${id}/reject`, { comments }).then(normalizeTask),
  return: (id: string, comments: string) => http.post<WorkflowTaskWire>(`/api/v1/workflow-tasks/${id}/return`, { comments }).then(normalizeTask),
};
